import { useState } from "react";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import type { Attachment } from "@theta/api-client";
import { deletePost, getPost, hidePost, unhidePost } from "@/lib/api/posts";
import { flagPost } from "@/lib/api/moderation";
import type { GroupRule } from "@/lib/api/groupRules";
import { useGroupRules } from "@/lib/hooks/useGroupRules";

interface UseContentModerationOptions {
  /** The post id being moderated — a post's own id, or a comment's (a comment
   *  IS a post row, and both are addressed at `/posts/{id}`). */
  postId: number | null | undefined;
  /**
   * Kept only so the four call sites compile unchanged: `POST
   * /posts/{id}/flags` DERIVES the group from the post, so a client can no
   * longer name the wrong one. Passing it still gates the affordance, which is
   * what every caller used it for.
   */
  groupId?: number;
  /** Danish subject for error messages, e.g. "Opslaget" / "Kommentaren". */
  subject: string;
  /** Invalidated after a successful delete, hide or unhide. */
  invalidateOnRemove: QueryKey[];
  onDeleted: () => void;
  onHidden: () => void;
  /**
   * Optional, unlike `onHidden`: a card that hides itself on hide has nothing
   * to do on unhide beyond the invalidation, which refetches it with
   * `isHidden: false` and a `can` block that offers Skjul again.
   */
  onUnhidden?: () => void;
}

/**
 * Delete/hide/unhide/flag/start-edit for a post or comment — the same actions
 * (and the same routes) either content type moderates itself with,
 * previously hand-duplicated with only the id source and Danish wording
 * differing between them.
 */
export function useContentModeration({
  postId,
  groupId,
  subject,
  invalidateOnRemove,
  onDeleted,
  onHidden,
  onUnhidden,
}: UseContentModerationOptions) {
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [editAttachments, setEditAttachments] = useState<Attachment[]>([]);
  const [editLoading, setEditLoading] = useState(false);
  const [showFlagForm, setShowFlagForm] = useState(false);
  const [flagReason, setFlagReason] = useState("");
  /** The rule the reporter points at (OSO-648); null = no citation. */
  const [flagRuleId, setFlagRuleId] = useState<GroupRule["id"] | null>(null);
  const [flagLoading, setFlagLoading] = useState(false);
  const [flagDone, setFlagDone] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  /**
   * The removal awaiting confirmation (OSO-663). Delete used to go through
   * `window.confirm` and hide asked nothing at all; both now open
   * `RemoveContentDialog`, which is also where a moderator cites the rule.
   */
  const [removal, setRemoval] = useState<"delete" | "hide" | null>(null);
  const [removalRuleId, setRemovalRuleId] = useState<GroupRule["id"] | null>(null);
  const [removalPending, setRemovalPending] = useState(false);

  /**
   * The group's rules, for the flag form's picker (OSO-648) and the removal
   * dialog's (OSO-663) — fetched only once one of them is actually open, so
   * a feed of fifty cards costs nothing. `groupId` finally earns its keep
   * here (see its comment above).
   */
  const rulesQuery = useGroupRules(groupId, {
    enabled: (showFlagForm || removal !== null) && groupId !== undefined,
  });
  const flagRules = rulesQuery.data ?? [];
  /**
   * The first open of either form pays for the fetch, so `[]` and "this group
   * wrote no rules" are the same value for a moment — and `FlagRuleSelect`
   * renders NOTHING for an empty list. Without this a moderator who confirms
   * promptly never saw that a citation was on offer. A disabled query reports
   * `isLoading: false`, so this is false until something actually opens.
   */
  const rulesLoading = rulesQuery.isLoading;

  const invalidate = () => {
    for (const queryKey of invalidateOnRemove) {
      queryClient.invalidateQueries({ queryKey });
    }
  };

  const startEdit = async () => {
    if (!postId) return;
    setEditLoading(true);
    setActionError(null);
    try {
      // `GET /posts/{id}` carries `bodyMarkdown` when the viewer may edit it,
      // and the attachments as ROWS — so the editor is seeded from one read
      // and never parses `![name](upload://…)` back out of a body (§1.12).
      const answer = await getPost(postId);
      /**
       * `unchanged` is unreachable here — this call sends no `ifChanged` — and
       * `data` is nullable only because the route is pollable (OSO-489). The
       * branch exists because the type is discriminated; `fetchMe` carries the
       * same one for the same reason.
       */
      const post = answer.unchanged ? null : answer.data;
      if (!post) throw new Error("no document");
      setEditText(post.bodyMarkdown ?? "");
      setEditAttachments(post.attachments);
      setIsEditing(true);
    } catch {
      setActionError(`${subject} kunne ikke hentes til redigering. Prøv igen.`);
    }
    setEditLoading(false);
  };

  /** Opens the confirm dialog; `confirmRemoval` is what actually writes. */
  const remove = () => {
    if (!postId) return;
    setActionError(null);
    setRemovalRuleId(null);
    setRemoval("delete");
  };

  const hide = () => {
    if (!postId) return;
    setActionError(null);
    setRemovalRuleId(null);
    setRemoval("hide");
  };

  const cancelRemoval = () => {
    setRemoval(null);
    setRemovalRuleId(null);
    setActionError(null);
  };

  const confirmRemoval = async () => {
    if (!postId || !removal) return;
    setRemovalPending(true);
    setActionError(null);
    try {
      if (removal === "delete") {
        await deletePost(postId, { ruleId: removalRuleId });
      } else {
        await hidePost(postId, { ruleId: removalRuleId });
      }
      invalidate();
      const done = removal === "delete" ? onDeleted : onHidden;
      setRemoval(null);
      setRemovalRuleId(null);
      done();
    } catch {
      // The dialog stays open with the error in it, so the retry is a click.
      setActionError(
        removal === "delete"
          ? `${subject} kunne ikke slettes. Prøv igen.`
          : `${subject} kunne ikke skjules. Prøv igen.`,
      );
    } finally {
      /**
       * `finally`, because `done()` above is the CALLER's callback and a
       * throw from it would otherwise leave this true for good — and while it
       * is true `ConfirmDialog` disables both buttons AND withholds
       * `onClose`, so the next removal opens a modal with no way out of it.
       */
      setRemovalPending(false);
    }
  };

  const unhide = async () => {
    if (!postId) return;
    setActionError(null);
    try {
      await unhidePost(postId);
      invalidate();
      onUnhidden?.();
    } catch {
      setActionError(`${subject} kunne ikke vises igen. Prøv igen.`);
    }
  };

  const flag = async () => {
    if (!postId || !groupId) return;
    setFlagLoading(true);
    setActionError(null);
    try {
      await flagPost(postId, flagReason || undefined, flagRuleId);
      setFlagDone(true);
      setShowFlagForm(false);
      setFlagRuleId(null);
    } catch {
      setActionError("Anmeldelsen kunne ikke sendes. Prøv igen.");
    }
    setFlagLoading(false);
  };

  return {
    isEditing,
    setIsEditing,
    editText,
    editAttachments,
    editLoading,
    startEdit,
    remove,
    hide,
    unhide,
    removal,
    removalRuleId,
    setRemovalRuleId,
    removalPending,
    confirmRemoval,
    cancelRemoval,
    showFlagForm,
    setShowFlagForm,
    flagReason,
    setFlagReason,
    flagRules,
    rulesLoading,
    flagRuleId,
    setFlagRuleId,
    flagLoading,
    flagDone,
    flag,
    actionError,
  };
}
